/**
 * Resolve default-location "lead" for the chat widget from either
 * `locations.publicGetLocationsForProject` (enriched `specialist`) or raw `project.projectLocation`
 * (TeamMemberLocation join) when the public locations query is empty or slow.
 */

export type WidgetLeadPreview = {
  name: string;
  title?: string;
  imageUrl?: string;
  isAvailable: boolean;
};

/** Strip Clerk org role slugs (e.g. org:external_collaborator) from visitor-facing widget copy. */
export function sanitizeWidgetLeadTitle(raw: unknown): string | undefined {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  if (s.startsWith("org:")) return undefined;
  return s;
}

export function listLocationsForWidget(
  existingFromApi: unknown[] | null | undefined,
  projectLocations: unknown[] | null | undefined,
): unknown[] {
  if (Array.isArray(existingFromApi) && existingFromApi.length > 0) return existingFromApi;
  if (Array.isArray(projectLocations) && projectLocations.length > 0) return projectLocations;
  return [];
}

function displayNameFromEmail(email: string): string {
  const e = email.trim();
  if (!e) return "";
  const at = e.indexOf("@");
  return at > 0 ? e.slice(0, at) : e;
}

/** True when default (or first) location has a lead worth showing in the widget. */
export function defaultLocationHasLeadAssignment(
  existingLocations: unknown[] | null | undefined,
  projectLocations: unknown[] | null | undefined,
): boolean {
  const list = listLocationsForWidget(existingLocations, projectLocations);
  const loc = pickDefaultLocation(list);
  if (!loc || typeof loc !== "object") return false;
  const o = loc as Record<string, unknown>;

  const spec = o.specialist;
  if (spec && typeof spec === "object") {
    const s = spec as Record<string, unknown>;
    if (s.systemAdmin === true) return false;
    return true;
  }

  const assignments = o.teamMemberLocations;
  if (Array.isArray(assignments)) {
    return assignments.some(
      (t: unknown) =>
        t && typeof t === "object" && (t as Record<string, unknown>).isSpecialist === true,
    );
  }

  const fn = o.responsiblePersonFirstName;
  const ln = o.responsiblePersonLastName;
  return Boolean(
    (typeof fn === "string" && fn.trim()) || (typeof ln === "string" && ln.trim()),
  );
}

function buildWidgetLeadFromLocation(loc: unknown): WidgetLeadPreview | null {
  if (!loc || typeof loc !== "object") return null;
  const o = loc as Record<string, unknown>;

  const spec = o.specialist;
  if (spec && typeof spec === "object") {
    const s = spec as Record<string, unknown>;
    let name = String(s.name ?? "").trim();
    const email = s.email ? String(s.email) : "";
    if (!name && email) name = displayNameFromEmail(email);
    const imageUrl = s.imageUrl ? String(s.imageUrl) : "";
    if (!name && !imageUrl) return null;
    const status = String(s.availabilityStatus ?? "UNAVAILABLE");
    const title =
      sanitizeWidgetLeadTitle(s.title) ??
      sanitizeWidgetLeadTitle(s.jobTitle) ??
      undefined;
    return {
      name: name || "Team member",
      title,
      imageUrl: imageUrl || undefined,
      isAvailable: status === "AVAILABLE",
    };
  }

  const assignments = o.teamMemberLocations;
  if (!Array.isArray(assignments)) return null;
  const specialistAssignment = assignments.find(
    (t: unknown) =>
      t && typeof t === "object" && (t as Record<string, unknown>).isSpecialist === true,
  ) as Record<string, unknown> | undefined;
  const tm = specialistAssignment?.teamMember as Record<string, unknown> | undefined;
  if (!tm || typeof tm !== "object") return null;
  const collab = tm.collaborator as Record<string, unknown> | undefined;
  const firstName = collab?.firstName ? String(collab.firstName) : "";
  const lastName = collab?.lastName ? String(collab.lastName) : "";
  const rawName = tm.name ? String(tm.name) : "";
  let displayName =
    rawName && !rawName.includes("@")
      ? rawName
      : `${firstName} ${lastName}`.trim();
  if (!displayName.trim() && tm.email) {
    displayName = displayNameFromEmail(String(tm.email));
  }
  const imageUrl = (tm.imageUrl ?? collab?.imageUrl) ? String(tm.imageUrl ?? collab?.imageUrl) : "";
  if (!displayName.trim() && !imageUrl) return null;
  const availability = collab?.availabilityStatus
    ? String(collab.availabilityStatus)
    : "UNAVAILABLE";
  const title =
    sanitizeWidgetLeadTitle(tm.title) ??
    sanitizeWidgetLeadTitle(collab?.title) ??
    undefined;
  return {
    name: displayName.trim() || "Team member",
    title,
    imageUrl: imageUrl || undefined,
    isAvailable: availability === "AVAILABLE",
  };
}

export function pickDefaultLocation(locations: unknown[]): unknown | null {
  if (!locations.length) return null;
  const def = locations.find(
    (l) => l && typeof l === "object" && (l as Record<string, unknown>).isDefaultLocation === true,
  );
  return def ?? locations[0] ?? null;
}

export function buildDefaultWidgetLead(
  existingLocations: unknown[] | null | undefined,
  projectLocations: unknown[] | null | undefined,
): WidgetLeadPreview | null {
  const list = listLocationsForWidget(existingLocations, projectLocations);
  const loc = pickDefaultLocation(list);
  return loc ? buildWidgetLeadFromLocation(loc) : null;
}

/** Quick-reply text when the visitor clicks the lead team member card in the widget. */
export function buildLeadTeamMemberClickMessage(lead: WidgetLeadPreview): string {
  const name = lead.name.trim() || "our team";
  const title = lead.title?.trim() || "team member";
  if (lead.isAvailable) {
    return `I need help from your ${title}, can you check if ${name} is available to chat?`;
  }
  return `I need help from your ${title}, can you take a message for ${name}?`;
}
